var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useMemo } from 'react';
import { FunctionColor, BrandColor } from '@lux/core';
import { Checkbox } from '@lux/form-elements';
import { AddS, SubtractS } from '@lux/zds-icons';
import { Preset, Text } from '@lux/text';
import { trackDeselectFilter, trackSelectFilter, trackViewFilter } from '@browsing/tracking';
import { AllInCategory } from '../all-in-category';
import * as S from './category.styles';
import { CategoryIcon } from '../category-icon';
var MAX_LEVEL = 3;
export var Category = function (_a) {
    var _b, _c, _d;
    var category = _a.category, parentCategory = _a.parentCategory, level = _a.level, selectedCategoriesSet = _a.selectedCategoriesSet, availableCategoriesSet = _a.availableCategoriesSet, isMyLoungeCrossCampaignEntry = _a.isMyLoungeCrossCampaignEntry, updateCategoriesFilter = _a.updateCategoriesFilter, expandedCategories = _a.expandedCategories, setExpandedCategories = _a.setExpandedCategories, isCrossCampaign = _a.isCrossCampaign, categoryIdLevel1 = _a.categoryIdLevel1, sharedGA4args = _a.sharedGA4args;
    var categoryId = category.id, parentCategoryId = category.parentId;
    var siblingCategoryIds = useMemo(function () { var _a; return ((_a = parentCategory.categories) === null || _a === void 0 ? void 0 : _a.map(function (_a) {
        var id = _a.id;
        return id;
    }).filter(function (id) { return categoryId !== id; })) || []; }, [parentCategory.categories, categoryId]);
    var isAllInCategorySelected = selectedCategoriesSet.has(categoryId) ||
        __spreadArray([categoryId], siblingCategoryIds, true).every(function (siblingCategoryId) {
            return selectedCategoriesSet.has(siblingCategoryId);
        });
    var toggleAllInCategory = useCallback(function () {
        updateCategoriesFilter(!isAllInCategorySelected ? [categoryId] : []);
        var filtersValue = level < 2 ? "".concat(categoryId) : "".concat(parentCategoryId, ";").concat(categoryId);
        if (isAllInCategorySelected) {
            trackDeselectFilter(__assign(__assign({}, sharedGA4args), { element: 'category_filter', filterLvl: level, filtersValue: filtersValue }));
        }
        else {
            trackSelectFilter(__assign(__assign({}, sharedGA4args), { element: 'category_filter', filterLvl: level, filtersValue: filtersValue }));
        }
    }, [
        isAllInCategorySelected,
        categoryId,
        updateCategoriesFilter,
        parentCategoryId,
        sharedGA4args,
        level,
    ]);
    var isExpanded = expandedCategories["level".concat(level)] === categoryId;
    var toggleExpanded = useCallback(function () {
        var nextExpandedCategories = __assign({}, expandedCategories);
        trackViewFilter(__assign(__assign({}, sharedGA4args), { element: 'category_filter', filterLvl: !isExpanded ? level + 1 : level, filtersValue: "".concat(categoryId) }));
        if (isExpanded) {
            nextExpandedCategories.level2 = null;
            if (level === 1) {
                nextExpandedCategories.level1 = null;
            }
            setExpandedCategories(nextExpandedCategories);
            return;
        }
        nextExpandedCategories["level".concat(level)] = categoryId;
        setExpandedCategories(nextExpandedCategories);
    }, [expandedCategories, isExpanded, categoryId, level, setExpandedCategories, sharedGA4args]);
    var isCategorySelected = selectedCategoriesSet.has(categoryId) || selectedCategoriesSet.has(parentCategoryId);
    var isAvailable = availableCategoriesSet.has(category.id);
    var toggleCategory = useCallback(function () {
        if (!isAvailable) {
            return;
        }
        if (isMyLoungeCrossCampaignEntry && level === 1 && isExpanded) {
            setExpandedCategories({ level1: categoryId, level2: null });
            updateCategoriesFilter([]);
            return;
        }
        if (level < MAX_LEVEL) {
            toggleExpanded();
            updateCategoriesFilter([]);
        }
        else {
            var selectedCategories = Array.from(selectedCategoriesSet);
            var filtersValue = "".concat(categoryIdLevel1, ";").concat(parentCategoryId);
            // If the all in button is selected, then select all the sibling categories apart from the category being clicked.
            if (selectedCategoriesSet.has(parentCategoryId)) {
                trackDeselectFilter(__assign(__assign({}, sharedGA4args), { element: 'category_filter', filterLvl: level, filtersValue: "".concat(filtersValue, ";").concat(siblingCategoryIds.join(',')) }));
                updateCategoriesFilter(siblingCategoryIds);
                return;
            }
            // If the category is being deselected, simply remove it.
            if (selectedCategoriesSet.has(categoryId)) {
                var nextSelectedCategories_1 = selectedCategories.filter(function (id) { return ![categoryId, parentCategoryId].includes(id); });
                trackDeselectFilter(__assign(__assign({}, sharedGA4args), { element: 'category_filter', filterLvl: level, filtersValue: "".concat(filtersValue, ";").concat(nextSelectedCategories_1.join(',')) }));
                updateCategoriesFilter(nextSelectedCategories_1);
                return;
            }
            // If all the siblings are already selected, then just select the all in button.
            if (siblingCategoryIds.length >= 1 &&
                siblingCategoryIds.every(function (siblingCategoryId) {
                    return selectedCategoriesSet.has(siblingCategoryId);
                })) {
                trackSelectFilter(__assign(__assign({}, sharedGA4args), { element: 'category_filter', filterLvl: level, filtersValue: "".concat(filtersValue, ";").concat(__spreadArray(__spreadArray([], siblingCategoryIds, true), [categoryId], false).join(',')) }));
                updateCategoriesFilter(!isAllInCategorySelected ? [parentCategoryId] : []);
                return;
            }
            // Otherwise, just add the category being selected.
            var nextSelectedCategories = __spreadArray(__spreadArray([], selectedCategories, true), [categoryId], false);
            trackSelectFilter(__assign(__assign({}, sharedGA4args), { element: 'category_filter', filterLvl: level, filtersValue: "".concat(filtersValue, ";").concat(nextSelectedCategories) }));
            updateCategoriesFilter(nextSelectedCategories);
        }
    }, [
        isMyLoungeCrossCampaignEntry,
        level,
        isExpanded,
        updateCategoriesFilter,
        selectedCategoriesSet,
        toggleExpanded,
        setExpandedCategories,
        categoryId,
        isAllInCategorySelected,
        siblingCategoryIds,
        parentCategoryId,
        sharedGA4args,
        categoryIdLevel1,
        isAvailable,
    ]);
    var isCurrentlyExpandedLevel = useMemo(function () {
        var level1 = expandedCategories.level1, level2 = expandedCategories.level2;
        if (!!level2 && level === 2) {
            return true;
        }
        if (!!level1 && !level2 && level === 1) {
            return true;
        }
        return false;
    }, [expandedCategories, level]);
    var isAnotherCategoryExpanded = !!expandedCategories["level".concat(level)];
    var isNotMaxLevel = level < MAX_LEVEL;
    if (isNotMaxLevel && isAnotherCategoryExpanded && !isExpanded) {
        return null;
    }
    var isMaxLevelCategory = level === MAX_LEVEL;
    var textColor = FunctionColor.function100;
    if (isExpanded && isNotMaxLevel) {
        textColor = FunctionColor.function60;
    }
    else if (isMaxLevelCategory && isCategorySelected) {
        textColor = BrandColor.primary;
    }
    var hasChildren = !!((_b = category === null || category === void 0 ? void 0 : category.categories) === null || _b === void 0 ? void 0 : _b.length);
    var hasMultipleChildren = ((_d = (_c = category.categories) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0) > 1;
    return (React.createElement(React.Fragment, null,
        React.createElement(S.ListItem, { key: category.id, onClick: toggleCategory, isExpanded: isExpanded && isNotMaxLevel, isNested: isExpanded && level < MAX_LEVEL - 1, "data-testid": "category-filter-small-screen-category", "data-selected": isCategorySelected },
            React.createElement(S.CategoryButton, __assign({}, (hasChildren && { 'aria-expanded': isExpanded })),
                React.createElement(S.CategoryName, null,
                    level > 1 && React.createElement(CategoryIcon, { id: category.id, withRightMargin: true }),
                    React.createElement(Text, { bold: isMaxLevelCategory && isCategorySelected, preset: Preset.S, color: textColor }, category.name)),
                hasChildren && isNotMaxLevel && (isExpanded ? React.createElement(SubtractS, null) : React.createElement(AddS, null)),
                isMaxLevelCategory && (React.createElement(Checkbox, { checked: isCategorySelected, readOnly: true, tabIndex: -1 })))),
        React.createElement("li", { key: "children-".concat(category.id) },
            React.createElement(S.List, null,
                isCurrentlyExpandedLevel && hasMultipleChildren && (React.createElement(AllInCategory, { categoryName: category.name, isSelected: isAllInCategorySelected, toggleAllInCategory: toggleAllInCategory })),
                isExpanded &&
                    isNotMaxLevel &&
                    category.categories &&
                    category.categories
                        .sort(function (a, b) {
                        return Number(availableCategoriesSet.has(b.id)) -
                            Number(availableCategoriesSet.has(a.id));
                    })
                        .map(function (c) { return (React.createElement(Category, { key: c.id, category: c, parentCategory: category, level: level + 1, availableCategoriesSet: availableCategoriesSet, selectedCategoriesSet: selectedCategoriesSet, isMyLoungeCrossCampaignEntry: isMyLoungeCrossCampaignEntry, updateCategoriesFilter: updateCategoriesFilter, expandedCategories: expandedCategories, setExpandedCategories: setExpandedCategories, isCrossCampaign: isCrossCampaign, categoryIdLevel1: categoryIdLevel1, sharedGA4args: sharedGA4args })); })))));
};
